import React, { useMemo, useState } from 'react';

// Simple responsive SVG bar chart for numeric values
// Props:
// - data: Array<{ label: string, value: number }>
// - height?: number (default 220)
// - barColor?: string (tailwind or hex)
export default function SimpleBarChart({ data = [], height = 220, barColor = '#6366f1' }) {
  const [hoverIndex, setHoverIndex] = useState(null);

  const { maxValue, bars } = useMemo(() => {
    const max = Math.max(0, ...data.map(d => Number(d.value) || 0));
    const safeMax = max === 0 ? 1 : max;
    const b = data.map(d => ({ ...d, value: Number(d.value) || 0 }));
    return { maxValue: safeMax, bars: b };
  }, [data]);

  const fmt = (n) => new Intl.NumberFormat('es-CL').format(n || 0);
  const width = Math.max(360, data.length * 40 + 40);
  const xPadding = 24;
  const chartWidth = width - xPadding * 2;
  const chartHeight = height - 40;
  const barGap = 12;
  const barWidth = Math.max(12, Math.min(36, chartWidth / (bars.length || 1) - barGap));

  return (
    <div className="w-full overflow-x-auto">
      <svg width={width} height={height} role="img" aria-label="Gráfico de barras">
        {Array.from({ length: 4 }).map((_, i) => {
          const y = 10 + (chartHeight * i) / 3;
          return (
            <line key={i} x1={xPadding} x2={xPadding + chartWidth} y1={y} y2={y} stroke="#e5e7eb" strokeWidth="1" />
          );
        })}

        {bars.map((d, i) => {
          const h = Math.round((d.value / maxValue) * (chartHeight - 10));
          const x = xPadding + i * (barWidth + barGap);
          const y = 10 + (chartHeight - h);
          const isHover = hoverIndex === i;
          return (
            <g key={i} onMouseEnter={() => setHoverIndex(i)} onMouseLeave={() => setHoverIndex(null)}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={h}
                rx={6}
                fill={barColor}
                opacity={isHover ? 0.9 : 0.8}
              />
              {h > 16 && (
                <text x={x + barWidth / 2} y={y - 6} textAnchor="middle" fontSize="10" fill="#374151">
                  {fmt(d.value)}
                </text>
              )}
              <text x={x + barWidth / 2} y={height - 10} textAnchor="middle" fontSize="11" fill="#6b7280">
                {String(d.label || '').toUpperCase()}
              </text>

              {isHover && (
                <g>
                  <rect x={x - 12} y={y - 34} width={Math.max(80, barWidth + 24)} height={24} rx={6} fill="#111827" opacity="0.9" />
                  <text x={x - 12 + Math.max(80, barWidth + 24) / 2} y={y - 18} textAnchor="middle" fontSize="11" fill="#fff">
                    {`${String(d.label).toUpperCase()}: ${fmt(d.value)}`}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
